import dynamic from 'next/dynamic';

// Above the Fold
import { useEffect } from 'react';
import Product from './sections/main/right/product';
import SellerInfo from './sections/main/right/shipping';
import Variants from './sections/main/right/variants';
import TopBar from './TopBar';
import BottomBar from './BottomBar';
import LeftSection from './sections/main/left';
import Breadcrumb from './sections/breadcrumb';
import TradeInBanner from '@/components/tradein/TradeInBanner';
import Metadata from './Metadata';
import Summary from './sections/main/right/product/Summary';
import MarketplaceV2 from '../common/Marketplace';

// Custom Hooks
import usePageScrollEvent from '../../collections/[handle]/NewProductPage/usePageScrollEvent';
import { trackViewed } from '@/lib/analytics/trackProduct';
import { useProductPageContext } from './hooks/useProductPageContext';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { isPurchaseFlowEnabled } from '@/lib/purchase-flow';
import FlashSaleBadge from './sections/FlashSaleBadge';
import ComesWith from './ComesWith';
import ReebeloCare from './ReebeloCare';
import { PayLaterProvider } from './sections/main/right/product/PayLaterMessage';

// Needs to be server side rendered for SEO
const DefaultProductSeo = dynamic(
  () => import('./sections/secondary/seo/DefaultProductSeo'),
  { ssr: true },
);

const MoreProductInfo = dynamic(
  () => import('./sections/secondary/MoreProductInfo'),
  { ssr: true },
);

const About = dynamic(() => import('./sections/main/right/product/about'), {
  ssr: true,
});

// Below the fold + No SEO impact - Load dynamically on client side
// Each loading placeholder holds the section's skeleton (or, for Reviews, its
// smallest rendered) height, measured per breakpoint.
const YouMayAlsoLike = dynamic(
  () => import('./sections/secondary/you-may-also-like'),
  {
    ssr: false,
    loading: () => <div className="h-[289px]" />,
  },
);

const PairItWith = dynamic(() => import('./sections/secondary/PairItWith'), {
  ssr: false,
  loading: () => <div className="h-[266px]" />,
});

const MoreFromBrand = dynamic(
  () => import('./sections/secondary/MoreFromBrand'),
  {
    ssr: false,
    loading: () => <div className="h-[286px]" />,
  },
);

const Reviews = dynamic(() => import('./sections/secondary/reviews'), {
  ssr: false,
  loading: () => (
    <div className="min-h-[1120px] xs:min-h-[990px] sm:min-h-[970px] lg:min-h-[745px]" />
  ),
});

const ProductPageLayout = () => {
  const { shouldShowTopBar } = usePageScrollEvent();
  const { customer } = useCustomerContext();
  const { product, selectedSku, selectedOffer, additionalInfo } =
    useProductPageContext();

  // Product Viewed Event
  useEffect(() => {
    if (!product || !selectedSku || !selectedOffer) {
      return;
    }
    trackViewed({
      v2: {
        product,
        sku: selectedSku,
        offer: selectedOffer,
        isOutOfStock: false, // If Selected SKU is defined, it means it's in stock
      },
      customer,
    });
  }, [product.id, selectedSku?.id, selectedOffer?.id, customer]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    const root = document.documentElement;

    root.style.setProperty('--cookie-banner-offset', '104px');

    return () => {
      root.style.removeProperty('--cookie-banner-offset');
    };
  }, []);

  const isFlashSale = !!selectedOffer?.featured;

  return (
    <>
      {/* Metadata  */}
      <Metadata />
      {/* Product Page */}
      <div className="bg-white text-sm">
        <TopBar shouldShowTopBar={shouldShowTopBar} />
        <BottomBar />
        <div className="xl:reebelo-container mx-auto pb-0 pt-5 lg:px-5 lg:py-5 xl:px-8">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-between px-5 lg:px-0">
            <Breadcrumb />
            {/* Tag ----- Mobile Only */}
            {isFlashSale && (
              <FlashSaleBadge className="rounded-full lg:hidden" />
            )}
          </div>
          {/* Main Section */}
          <section className="relative mt-2 grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
            {/* Left Section */}
            <LeftSection shouldShowTopBar={shouldShowTopBar} />
            {/* Right Section */}
            <PayLaterProvider>
              <div className="flex w-full flex-col gap-y-4">
                {isFlashSale && (
                  <FlashSaleBadge className="absolute -top-5 right-5 hidden rounded-t lg:block" />
                )}
                <Product />
                <Variants />
                <ReebeloCare />
                <ComesWith />
                <SellerInfo />
                <About />
                {/* The purchase flow has it below the variants instead. */}
                {!isPurchaseFlowEnabled(customer?.email) && (
                  <TradeInBanner className="px-5 lg:px-0" />
                )}
                <Summary />
              </div>
            </PayLaterProvider>
          </section>
          {/* Secondary Section */}
        </div>
        {/* Carousels */}
        <section className="mt-10 flex flex-col gap-y-10 pb-10">
          <MoreProductInfo />
          <PairItWith psku={product?.psku ?? ''} />
          <YouMayAlsoLike />
          <MoreFromBrand />
          <div>
            <MarketplaceV2
              categoryWarranty={additionalInfo.categoryWarrantyInfo}
            />
          </div>
          <Reviews />
          <DefaultProductSeo />
        </section>
      </div>
    </>
  );
};

export default ProductPageLayout;
